import { computed, action, observable } from 'mobx'
import {
  RootStore, BRAND_STORE, EDITOR_STORE,
  EDITOR_MONITOR_STORE, PROJECT_STORE, PERSON_STORE
} from 'store'
import { popupSelectorTypes } from './types'
import { Brand, Person } from 'models'

export const EDITOR_ITEMS_HANDLER_STORE = 'EDITOR_ITEMS_HANDLER_STORE'

export class EditorItemsHandlerStore {
  rootStore: RootStore

  constructor (rootStore: RootStore) {
    this.rootStore = rootStore
  }

  @observable selectedObjectsGroupedByDropdownSelection: any = {}
  @observable dropdownSelection: string = ''
  @observable popupSelector: string = ''

  private onBrandSelect (brandName) {
    const brandId = this.getBrandIdByName(brandName)
    if (brandId) {
      this.setDropdownSelection(brandName)
      this.rootStore[BRAND_STORE].getBrandById(brandId)
    }
  }

  private setDropdownSelection (value = '') {
    this.dropdownSelection = value
  }

  private setSelectedObjectsGroupedByDropdownSelection (obj = {}) {
    this.selectedObjectsGroupedByDropdownSelection = obj
  }

  private getEntityReadonlyStatus (entity) {
    return !!this.rootStore[EDITOR_MONITOR_STORE].getShotsCountForEntity(entity)
  }

  private getBrandIdByName (brandName: string) {
    const brand = this.rootStore[BRAND_STORE].brandsList.find(b => b.name === brandName)
    return brand ? brand.id : null
  }

  @computed get brandNames () {
    return this.rootStore[BRAND_STORE].brandsList.map(b => b.name)
  }

  @computed get selectedBrandProducts () {
    const products = this.rootStore[BRAND_STORE].brandToView.products || []
    return products.map(p => {
      return {
        ...p,
        readOnly: this.getEntityReadonlyStatus(p)
      }
    })
  }

  @computed get selectedPersons () {
    return this.rootStore[EDITOR_STORE][this.dropdownSelection] as Person[]
  }

  @computed get selectedProducts () {
    const brand = this.rootStore[EDITOR_STORE].brands
      .find(b => b.id === this.rootStore[BRAND_STORE].brandToView.id) || {} as Brand
    return brand.products || []
  }

  @computed get selectedObjectsList () {
    if (!this.dropdownSelection) return []

    if (this.dropdownSelection &&
      this.selectedObjectsGroupedByDropdownSelection[this.dropdownSelection]) {
      return this.selectedObjectsGroupedByDropdownSelection[this.dropdownSelection]
    }

    switch (this.popupSelector) {
      case popupSelectorTypes.addProduct:
        return this.selectedProducts
      case popupSelectorTypes.addPerson:
        return this.selectedPersons
      default: return []
    }
  }

  @computed get dropdownOptions () {
    switch (this.popupSelector) {
      case popupSelectorTypes.addProduct:
        return this.brandNames
      case popupSelectorTypes.addPerson:
        return ['actors', 'directors', 'producers']
      default: return []
    }
  }

  @computed get dropdownLabel () {
    switch (this.popupSelector) {
      case popupSelectorTypes.addProduct:
        return 'Brand'
      case popupSelectorTypes.addPerson:
        return 'Persons in role'
      default: return ''
    }
  }

  @computed get dropdownPlaceholder () {
    switch (this.popupSelector) {
      case popupSelectorTypes.addProduct:
        return 'Select brand'
      case popupSelectorTypes.addPerson:
        return 'Select role'
      default: return ''
    }
  }

  @computed get dropdownSettings () {
    return {
      options: this.dropdownOptions,
      label: this.dropdownLabel,
      placeholder: this.dropdownPlaceholder
    }
  }

  @computed get personsList () {
    const persons = this.rootStore[PERSON_STORE].personsList || []
    return persons.map(p => {
      return {
        ...p,
        readOnly: this.getEntityReadonlyStatus(p)
      }
    })
  }

  @computed get objectList () {
    switch (this.popupSelector) {
      case popupSelectorTypes.addProduct:
        return this.selectedBrandProducts
      case popupSelectorTypes.addPerson:
        return this.dropdownSelection ? this.personsList : []
      default: return []
    }
  }

  @action.bound setPopupSelector (value = '') {
    if (value === this.popupSelector) return
    this.popupSelector = value
  }

  @action.bound onProductListChange (list) {
    this.selectedObjectsGroupedByDropdownSelection[this.dropdownSelection] = list
    // console.log('list', list)
  }

  submitProductsChange = () => {
    const project = { ...this.rootStore[EDITOR_STORE].project }
    const brands = [...project.brands]
      .filter(b => {
        if (this.selectedObjectsGroupedByDropdownSelection[b.name] &&
          this.selectedObjectsGroupedByDropdownSelection[b.name].length === 0) {
          return false
        }
        return true
      })
      .map(b => this.selectedObjectsGroupedByDropdownSelection[b.name]
        ? ({ ...b, products: this.selectedObjectsGroupedByDropdownSelection[b.name] })
        : b
      )

    let productsFromNewBrands = []
    Object.keys(this.selectedObjectsGroupedByDropdownSelection)
      .filter(brandName => !brands.some(b => b.name === brandName))
      .forEach(brandName => {
        productsFromNewBrands = productsFromNewBrands
          .concat(this.selectedObjectsGroupedByDropdownSelection[brandName])
      })

    this.rootStore[PROJECT_STORE]
      .updateProject({ ...project, brands })
      .then(res => {
        this.rootStore[EDITOR_STORE].updateProjectAndEntities(res)

        if (productsFromNewBrands.length) {
          this.rootStore[EDITOR_STORE].addEntities({ products: productsFromNewBrands })
        }

        this.clearStoreObservables()
      })
  }

  submitPersonsChange = () => {
    const project = { ...this.rootStore[EDITOR_STORE].project }
    const actors = this.selectedObjectsGroupedByDropdownSelection.actors || [...project.actors]
    const directors = this.selectedObjectsGroupedByDropdownSelection.directors ||
      [...project.directors]
    const producers = this.selectedObjectsGroupedByDropdownSelection.producers ||
      [...project.producers]

    this.rootStore[PROJECT_STORE]
      .updateProject({ ...project, actors, directors, producers })
      .then(res => {
        this.rootStore[EDITOR_STORE].updateProjectAndEntities(res)
        this.clearStoreObservables()
      })
  }

  submitEntitiesChange = () => {
    switch (this.popupSelector) {
      case popupSelectorTypes.addProduct:
        this.submitProductsChange()
        break
      case popupSelectorTypes.addPerson:
        this.submitPersonsChange()
        break
      default: break
    }
  }

  dropdownOnSelect = ([value]) => {
    switch (this.popupSelector) {
      case popupSelectorTypes.addProduct:
        this.onBrandSelect(value)
        break
      case popupSelectorTypes.addPerson:
        this.setDropdownSelection(value)
        break
      default: break
    }
  }

  clearStoreObservables = () => {
    this.setDropdownSelection()
    this.setSelectedObjectsGroupedByDropdownSelection()
  }
}
